import React from 'react';
import '../../styles/legal.css';

const PrivacyPolicy = () => {
    return (
        <div className="legal-container">
            <div className="legal-header">
                <h1 className="legal-title">Privacy Policy</h1>
                <p className="legal-meta">Last Updated: June 3, 2026</p>
            </div>

            <div className="legal-section">
                <h2>1. Introduction</h2>
                <p>
                    SIE QR Menu ("we", "our", "us") values your privacy. This Privacy Policy details how Search is End! 
                    (operating under registered proprietary firm <strong>Smart City Ajmer</strong>) collects, utilizes, 
                    shares, and protects your information when you register for, access, or use our digital QR menu SaaS platform.
                </p>
                <p>
                    By using the Platform, you consent to the collection and use of information in accordance with this policy.
                </p>
            </div>

            <div className="legal-section">
                <h2>2. Information We Collect</h2>
                <p>
                    We collect personal, transactional, and device-level information to ensure a functional and optimized SaaS experience:
                </p>
                
                <h3>A. Information You Provide Directly</h3>
                <ul>
                    <li><strong>Account Details:</strong> When you sign up, we collect your name, restaurant name, email address, phone number, and account credentials.</li>
                    <li><strong>Restaurant Configuration:</strong> Menu items, descriptions, category groupings, pricing, custom themes, active discount codes, and operational variables (e.g., dine-in or delivery status).</li>
                    <li><strong>Payment Metadata:</strong> Transaction records, subscription plans, amounts paid, and payment methods. We do not store raw credit card details; these are processed securely by our certified third-party payment gateways.</li>
                </ul>

                <h3>B. Information Collected from End Customers (Menu Visitors)</h3>
                <ul>
                    <li><strong>Order Details:</strong> Items ordered, quantities, custom table numbers, dine-in vs. takeaway preferences, order timestamp, and total billing amounts.</li>
                </ul>

                <h3>C. Passive Tracking & System Logs</h3>
                <p>
                    To compile restaurant dashboards, analyze platform traffic, and maintain stability, we collect logs passively:
                </p>
                <ul>
                    <li><strong>Device Fingerprints & Tokens:</strong> Firebase Cloud Messaging (FCM) tokens to facilitate push notification delivery.</li>
                    <li><strong>Usage Data:</strong> Page URLs visited, referrers, navigation patterns, item clicks, and feature engagement metrics.</li>
                    <li><strong>Technical Details:</strong> IP addresses, browser applications, device types, screen dimensions, and operating system platforms.</li>
                </ul>
            </div>

            <div className="legal-section">
                <h2>3. How We Use the Information</h2>
                <p>
                    We utilize the collected information for the following purposes:
                </p>
                <ul>
                    <li><strong>Platform Delivery:</strong> Hosting your public menu page, processing live orders, generating QR codes, and displaying administrative panels.</li>
                    <li><strong>Analytics & Reporting:</strong> Constructing the performance dashboards shown to restaurant owners, detailing visitor traffic trends, growth charts, menu-item popularity, and device distributions.</li>
                    <li><strong>Staff Management:</strong> Securing access levels for waiters, kitchen display displays (KOT), and managers.</li>
                    <li><strong>Communications:</strong> Sending administrative updates, billing invoices, system alerts, promotional updates, and push notifications to subscribers.</li>
                    <li><strong>Security & Compliance:</strong> Defending against fraudulent registrations, abuse, unauthorized access, and malicious scripts.</li>
                </ul>
            </div>

            <div className="legal-section">
                <h2>4. Superadmin Access & Privacy Protections</h2>
                <p>
                    Due to the architecture of our SaaS database, authorized Superadministrators of Search is End! (Smart City Ajmer) 
                    have structural access to all customer accounts, databases, logs, and server records.
                </p>
                <div className="legal-alert">
                    <p>
                        Superadmin access is governed by strict protocols. We do not inspect, edit, or copy your operational data unless 
                        specifically requested by you for customer support, system optimization, maintenance audits, or when legally compelled by law. 
                        We do not sell, rent, or lease your restaurant's order histories or customer data to third-party marketing brokers.
                    </p>
                </div>
            </div>

            <div className="legal-section">
                <h2>5. Third-Party Sharing</h2>
                <p>
                    We share details with selected third parties strictly to enable platform services:
                </p>
                <ul>
                    <li><strong>Firebase Cloud Messaging (FCM) by Google:</strong> Used to transmit promotional alerts and order status changes to devices subscribing to notifications.</li>
                    <li><strong>Payment Gateways:</strong> Used to securely authorize, process, and settle subscription payments.</li>
                    <li><strong>Cloud Hosting Partners:</strong> Database and server storage hosting facilities to keep the application online.</li>
                </ul>
            </div>

            <div className="legal-section">
                <h2>6. Cookies & Local Storage</h2>
                <p>
                    We use cookies and browser local storage to maintain session states, save system settings (such as dark mode preferences), 
                    keep administrators logged in, and facilitate cart persistence for menu visitors. You may configure your browser to block 
                    cookies, though some parts of the service may fail to function correctly.
                </p>
            </div>

            <div className="legal-section">
                <h2>7. Your Rights & Data Choices</h2>
                <p>
                    You have the following rights regarding your data:
                </p>
                <ul>
                    <li><strong>Correction:</strong> Modify your profile, billing info, and menu items instantly via the dashboard.</li>
                    <li><strong>Cancellation:</strong> Terminating your subscription blocks access. You can request permanent deletion of your data logs by emailing us.</li>
                    <li><strong>Opt-Out:</strong> Customers can revoke push notification permissions in their browser settings at any time.</li>
                </ul>
            </div>

            <div className="legal-section">
                <h2>8. Data Security</h2>
                <p>
                    We implement industry-standard administrative, physical, and digital safeguards (such as SSL encryption, hashed password storage, 
                    and bearer tokens) to protect your information. However, no electronic storage method is 100% secure, and we cannot guarantee 
                    absolute security.
                </p>
            </div>

            <div className="legal-section">
                <h2>9. Changes to This Privacy Policy</h2>
                <p>
                    We may update this Privacy Policy periodically. We will alert you of modifications by publishing the updated text here and 
                    revising the "Last Updated" date. Continued usage of the Platform constitutes your consent to the changes.
                </p>
            </div>

            <div className="legal-contact-box">
                <h3>Contact & Company Details</h3>
                <p><strong>Operating Brand:</strong> Search is End!</p>
                <p><strong>Registered Proprietorship:</strong> Smart City Ajmer</p>
                <p><strong>GST Number:</strong> 08ANVPS9429D1Z8</p>
                <p><strong>Support & Legal Email:</strong> <a href="mailto:searchisend@smartcityajmer.com">searchisend@smartcityajmer.com</a></p>
                <p><strong>Mailing Address:</strong> Registered address of Smart City Ajmer (Ajmer, Rajasthan, India).</p>
                <p style={{ fontSize: '0.85em', color: '#64748b', marginTop: '15px' }}>
                    <em>Disclaimer: If the registered physical address of Smart City Ajmer is updated, this mailing address is deemed updated in tandem. 
                    For the latest active registered address, please verify the registration certificate on the official Smart City Ajmer website.</em>
                </p>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
